import type { Split } from '@tsagko/label-contracts'

/**
 * Axis-aligned bounding box in any consistent unit system. The helper is
 * unit-agnostic — callers pass mm, dots, pixels, or any other unit; the
 * children come back in the same unit.
 */
export interface Box {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Compute the two child boxes when a parent zone is divided by a split.
 * Single source of truth for split semantics — both the ZPL pipeline
 * (`flatten-zones.ts`) and the designer canvas (`apps/designer/src/canvas/layout.ts`)
 * import this.
 *
 * Semantics (CSS flexbox convention):
 *   - 'row':    children flow LEFT → RIGHT. Divider runs vertically.
 *               children[0] occupies the LEFT band, children[1] the RIGHT.
 *   - 'column': children flow TOP → BOTTOM. Divider runs horizontally.
 *               children[0] occupies the TOP band, children[1] the BOTTOM.
 *
 * `ratio ∈ (0, 1)` is applied to children[0]'s share of the parent's main
 * axis (width for 'row', height for 'column'). The helper does NOT
 * clamp or validate the ratio — pre-validated by the Zod schema upstream.
 *
 * Pure. No mutation. Both boxes always sum to the parent's main-axis
 * length and share the parent's cross-axis length.
 */
export function splitChildBoxes(parent: Box, split: Split): [Box, Box] {
  if (split.direction === 'column') {
    // CSS-column: children flow TOP → BOTTOM. Divide height.
    const hA = parent.h * split.ratio
    return [
      { x: parent.x, y: parent.y,      w: parent.w, h: hA },
      { x: parent.x, y: parent.y + hA, w: parent.w, h: parent.h - hA },
    ]
  }
  // 'row': children flow LEFT → RIGHT. Divide width.
  const wA = parent.w * split.ratio
  return [
    { x: parent.x,      y: parent.y, w: wA,            h: parent.h },
    { x: parent.x + wA, y: parent.y, w: parent.w - wA, h: parent.h },
  ]
}
